import { ErrorState, LoadingState } from '@/components/ui/async-state';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { AppScreen, Card, MetaBadge, ScreenHeader } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/lib/auth/auth';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { useOrganizationInvitationActions } from '@/features/workspaces/useOrganizationInvitationActions';
import { useOrganizationInvitationPreview } from '@/features/workspaces/useOrganizationInvitationPreview';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { ArrowLeftIcon, MailIcon, UsersIcon } from 'lucide-react-native';
import { View } from 'react-native';

function formatRole(role: string) {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export default function InvitationScreen() {
  const session = useAuth((state) => state.session);
  const { token } = useLocalSearchParams<{ token?: string }>();
  const invitationToken = typeof token === 'string' ? token : '';
  const previewQuery = useOrganizationInvitationPreview(
    invitationToken,
    invitationToken.length > 0
  );
  const { acceptMutation, declineMutation } = useOrganizationInvitationActions(invitationToken);

  if (!invitationToken) {
    return <Redirect href={'/(tabs)/events' as any} />;
  }

  return (
    <AppScreen>
      <View className="flex-1 px-4 pt-4">
        <View className="mb-6 flex-row items-center justify-between">
          <Button variant="ghost" onPress={() => router.back()}>
            <Icon as={ArrowLeftIcon} size={16} color="white" />
            <Text className="font-semibold">Back</Text>
          </Button>
        </View>

        {previewQuery.isPending ? <LoadingState label="Loading invitation..." /> : null}

        {previewQuery.isError ? (
          <ErrorState
            body={getAPIErrorRecoveryCopy(
              previewQuery.error,
              'workspace',
              'Unable to load invitation.'
            )}
            onAction={() => previewQuery.refetch()}
          />
        ) : null}

        {previewQuery.data ? (
          <>
            <ScreenHeader
              eyebrow="Invitation"
              title={previewQuery.data.organization_name}
              subtitle="Accept this invite to open the team and venue access tied to your role."
              action={<MetaBadge label={formatRole(previewQuery.data.role)} tone="warm" />}
            />

            <Card className="mt-6 gap-4" tone="featured">
              <View className="flex-row items-center gap-2">
                <Icon as={UsersIcon} size={16} color="white" />
                <Text className="text-lg font-semibold text-foreground">Role</Text>
              </View>
              <Text variant="muted" className="leading-6">
                You were invited as {formatRole(previewQuery.data.role).toLowerCase()} for{' '}
                {previewQuery.data.organization_name}.
              </Text>
              <View className="flex-row items-center gap-2">
                <Icon as={MailIcon} size={16} color="white" />
                <Text className="font-semibold text-foreground">{previewQuery.data.email}</Text>
              </View>
              {previewQuery.data.invited_by ? (
                <Text variant="muted" className="leading-6">
                  Sent by {previewQuery.data.invited_by}.
                </Text>
              ) : null}
              {previewQuery.data.venues.length > 0 ? (
                <View className="gap-2">
                  <Text className="text-sm font-semibold text-foreground">Venues in this team</Text>
                  {previewQuery.data.venues.map((venue) => (
                    <View
                      key={venue.venue_id}
                      className="rounded-[18px] border border-border bg-card/70 px-4 py-3">
                      <Text className="font-semibold text-foreground">{venue.venue_name}</Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </Card>

            {!session ? (
              <Card className="mt-4 gap-3" tone="inset">
                <Text className="text-lg font-semibold text-foreground">Sign in to continue</Text>
                <Text variant="muted" className="leading-6">
                  Invitations can only be accepted by the account whose email matches this invite.
                </Text>
                <Button
                  onPress={() =>
                    router.push({
                      pathname: '/sign-in',
                      params: { next: `/invitations/${invitationToken}` },
                    })
                  }>
                  <Text className="font-semibold">Sign in</Text>
                </Button>
              </Card>
            ) : null}

            {session ? (
              <View className="mt-4 gap-3">
                <Button
                  disabled={acceptMutation.isPending}
                  onPress={() =>
                    acceptMutation.mutate(undefined, {
                      onSuccess: () => {
                        router.replace('/workspaces' as never);
                      },
                    })
                  }>
                  <Text className="font-semibold">
                    {acceptMutation.isPending ? 'Accepting…' : 'Accept invitation'}
                  </Text>
                </Button>

                <Button
                  variant="outline"
                  disabled={declineMutation.isPending}
                  onPress={() =>
                    declineMutation.mutate(undefined, {
                      onSuccess: () => {
                        router.replace('/(tabs)/events');
                      },
                    })
                  }>
                  <Text className="font-semibold">
                    {declineMutation.isPending ? 'Declining…' : 'Decline invitation'}
                  </Text>
                </Button>

                {acceptMutation.error ? (
                  <ErrorState
                    title="Unable to accept invitation."
                    body={getAPIErrorRecoveryCopy(
                      acceptMutation.error,
                      'workspace',
                      'Unable to accept invitation.'
                    )}
                  />
                ) : null}

                {declineMutation.error ? (
                  <ErrorState
                    title="Unable to decline invitation."
                    body={getAPIErrorRecoveryCopy(
                      declineMutation.error,
                      'workspace',
                      'Unable to decline invitation.'
                    )}
                  />
                ) : null}
              </View>
            ) : null}
          </>
        ) : null}
      </View>
    </AppScreen>
  );
}
